/**
 * "Learn Medsender" tutorial UI state.
 *
 * Only transient UI state lives here — which panel is open and which tutorial
 * is running. Durable progress lives in localStorage (see
 * components/tutorial/tutorialProgress.js) and the `has_completed_tutorial`
 * summary flag lives on the user record. It reaches redux through the userProfile
 * reducer: `FINISHED_GET_USER_INFO` hydrates it from `GET /api/v1/users`, and
 * `FINISHED_UPDATE_USER_DETAILS` mirrors it back after a successful PATCH. (This
 * used to name only the latter case, which at the time did not carry the field at
 * all — so the flag appeared to be synced when nothing read it.)
 */

/** Initial State */
const initialState = {
  menuOpen: false,
  activeTutorialId: null,
  // A launch requested from somewhere that can't navigate or wait for targets
  // itself (the Guide page's tutorial cards). LearnMedsender is mounted app-wide
  // with router access, so it picks this up and runs the real start sequence.
  pendingTutorialId: null,
  // Guards the post-walkthrough auto-open so it can only ever fire once per
  // mounted session, even if props settle in several passes.
  autoOpenEvaluated: false,
};

// eslint-disable-next-line default-param-last
export default (state = initialState, action) => {
  switch (action.type) {
    case 'OPEN_TUTORIAL_MENU':
      return {
        ...state,
        menuOpen: true,
      };
    case 'CLOSE_TUTORIAL_MENU':
      return {
        ...state,
        menuOpen: false,
      };
    case 'REQUEST_TUTORIAL':
      return {
        ...state,
        menuOpen: false,
        pendingTutorialId: action.tutorialId || null,
      };
    case 'CLEAR_PENDING_TUTORIAL':
      return {
        ...state,
        pendingTutorialId: null,
      };
    case 'START_TUTORIAL':
      return {
        ...state,
        // The checklist gets out of the way while a tutorial runs so it can't
        // sit on top of the element being spotlighted.
        menuOpen: false,
        // The request (if any) has now been acted on.
        pendingTutorialId: null,
        activeTutorialId: action.tutorialId || null,
      };
    case 'STOP_TUTORIAL':
      return {
        ...state,
        activeTutorialId: null,
      };
    case 'TUTORIAL_FINISHED':
      return {
        ...state,
        activeTutorialId: null,
        // Returning to the checklist after finishing a tutorial shows the new
        // checkmark and nudges the next item.
        menuOpen: action.reopenMenu !== false,
      };
    case 'TUTORIAL_AUTO_OPEN_EVALUATED':
      return {
        ...state,
        autoOpenEvaluated: true,
      };
    // Nothing here may survive the auth boundary. Signing out is an in-app
    // dispatch followed by `push('/login')` (actions/auth.js#logout), so the
    // store is never torn down: without this, a second account signing in on the
    // same tab inherits the first account's open checklist, its running or
    // pending tutorial, and — worst — its spent auto-open latch, which silently
    // suppresses the post-walkthrough checklist for the new user.
    //
    // Both cases reset auth to its default state (reducers/auth.js), so both
    // must reset here. Written as literals rather than importing `USER_AUTH`
    // ('LOGGED_OUT' / 'USER_LOGOUT_FAILED') to keep this reducer's unit test
    // from pulling in actions/auth.js and its axios/Sentry/posthog imports —
    // the same convention records.js and inboxTableReducers.js follow.
    case 'LOGGED_OUT':
    case 'USER_LOGOUT_FAILED':
      return initialState;
    default:
      return state;
  }
};
